'use client';

import Image from 'next/image';
import Link from 'next/link';
import { motion } from 'framer-motion';
import {
  Mail,
  Phone,
  ShieldCheck,
  ArrowRight,
  Target,
  Heart,
  Lightbulb,
  Building2,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PAGE_HERO_IMAGES } from '@/lib/marketing-images';

const milestones = [
  { year: '2018', title: 'Founded in Johannesburg', desc: 'GlonexTrack launched from Sandton with a vision to modernize fleet telematics in Africa.' },
  { year: '2019', title: 'First 1,000 Vehicles', desc: 'Reached our first major milestone with logistics companies across South Africa.' },
  { year: '2020', title: 'Camera Telematics Launch', desc: 'Introduced quad-camera HLS streaming with ADAS and DMS capabilities.' },
  { year: '2021', title: 'Pan-African Expansion', desc: 'Deployed across Nigeria, Kenya, Ghana, and Egypt. Opened regional offices.' },
  { year: '2022', title: 'Insurance Partnership', desc: 'Launched UBI risk scoring APIs. Partnered with 14 insurer countries.' },
  { year: '2023', title: 'AI Copilot & 100K Vehicles', desc: 'Released AI-powered anomaly detection and predictive maintenance. Crossed 100K vehicles.' },
  { year: '2024', title: 'European & Gulf Markets', desc: 'Expanded to UK, Netherlands, UAE, and Saudi Arabia. Achieved ISO 27001 roadmap.' },
  { year: '2025', title: 'Enterprise Platform', desc: 'Full SaaS platform with multi-tenant architecture, 100+ reports, and white-label CMS.' },
];

const values = [
  { icon: Target, title: 'Mission-Driven', desc: 'We believe every fleet deserves world-class technology, regardless of geography or size.' },
  { icon: Lightbulb, title: 'Innovation First', desc: 'We push the boundaries of telematics with AI, camera intelligence, and real-time streaming.' },
  { icon: Heart, title: 'Customer Obsessed', desc: 'Our success is measured by the operational improvements and safety gains of our customers.' },
  { icon: ShieldCheck, title: 'Trust & Security', desc: 'Zero Trust architecture, POPIA/GDPR compliance, and end-to-end encryption by default.' },
];

const team = [
  { name: 'Thabo Modise', role: 'CEO & Co-founder', bio: 'Former fleet operations director. Passionate about African tech innovation.' },
  { name: 'Naledi Khumalo', role: 'CTO & Co-founder', bio: 'Ex-Google engineer. Architect of the GlonexTrack edge-to-cloud platform.' },
  { name: 'David van der Berg', role: 'VP of Engineering', bio: 'Scaled telematics systems serving millions of data points per day.' },
  { name: 'Amara Okafor', role: 'Head of Product', bio: 'Product leader who bridges fleet operator needs with cutting-edge technology.' },
  { name: 'Sarah Mitchell', role: 'Head of Customer Success', bio: 'Ensures every customer achieves measurable fleet performance improvements.' },
  { name: 'Raj Patel', role: 'Head of AI & ML', bio: 'PhD in machine learning. Leads predictive analytics and anomaly detection.' },
];

const compliance = [
  { label: 'POPIA Compliant', desc: 'South African data protection compliance.' },
  { label: 'GDPR Compliant', desc: 'European data privacy regulation adherence.' },
  { label: 'ISO 27001 Roadmap', desc: 'Information security management standard.' },
  { label: 'Zero Trust Architecture', desc: 'Network security framework with no implicit trust.' },
  { label: 'SOC 2 Type II', desc: 'Service organization security controls (in progress).' },
  { label: 'End-to-End Encryption', desc: 'Data encrypted in transit and at rest.' },
];

export default function AboutPage() {
  return (
    <>
      {/* ── Hero ─────────────────────────────────────── */}
      <section className="gradient-hero relative overflow-hidden py-20 lg:py-28">
        <div className="absolute -top-40 -right-40 h-[400px] w-[400px] rounded-full bg-emerald-200/20 blur-[100px]" />
        <div className="relative z-10 mx-auto max-w-7xl px-6 text-center lg:px-8">
          <Badge variant="accent" className="mb-4">Our Story</Badge>
          <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl">
            Born in Johannesburg.{' '}
            <span className="text-gradient">Deployed globally.</span>
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-300">
            GlonexTrack started with a simple belief: African innovation can lead the world in
            fleet telematics intelligence. Today we serve 120,000+ vehicles across 14 countries.
          </p>
          <div className="mx-auto mt-8 max-w-4xl">
            <div className="glass-card-heavy rounded-3xl p-2 border border-white/10">
              <Image
                src={PAGE_HERO_IMAGES.about.src}
                alt={PAGE_HERO_IMAGES.about.alt}
                width={1600}
                height={900}
                className="h-auto w-full rounded-2xl object-cover"
                priority
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── Values ───────────────────────────────────── */}
      <section className="bg-background py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto mb-16 max-w-2xl text-center">
            <h2 className="text-3xl font-bold text-white">What We Stand For</h2>
            <p className="mt-3 text-base text-slate-300">
              Our core values guide every decision, feature, and customer interaction.
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((val, i) => (
              <motion.div
                key={val.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.4 }}
                className="rounded-2xl border border-white/10 bg-background p-8 text-center glass-card"
              >
                <div className="mx-auto mb-4 inline-flex rounded-xl bg-neon-cyan/10 p-3 text-neon-cyan">
                  <val.icon className="h-6 w-6" />
                </div>
                <h3 className="text-base font-semibold text-white">{val.title}</h3>
                <p className="mt-2 text-sm text-slate-300">{val.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Timeline ─────────────────────────────────── */}
      <section className="bg-white/5 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto mb-16 max-w-2xl text-center">
            <h2 className="text-3xl font-bold text-white">Our Journey</h2>
            <p className="mt-3 text-base text-slate-300">
              From a Sandton startup to a global telematics platform.
            </p>
          </div>
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-4 top-0 bottom-0 w-px bg-emerald-200 lg:left-1/2" />

            <div className="space-y-8">
              {milestones.map((ms, i) => (
                <motion.div
                  key={ms.year}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className={`relative flex items-start gap-6 lg:gap-12 ${
                    i % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                  }`}
                >
                  {/* Dot */}
                  <div className="absolute left-4 top-2 h-3 w-3 -translate-x-1/2 rounded-full bg-neon-cyan/100 ring-4 ring-white lg:left-1/2" />

                  {/* Content */}
                  <div className={`flex-1 pl-10 lg:pl-0 ${i % 2 === 0 ? 'lg:text-right lg:pr-16' : 'lg:text-left lg:pl-16'}`}>
                    <span className="text-sm font-bold text-neon-cyan">{ms.year}</span>
                    <h3 className="text-lg font-semibold text-white">{ms.title}</h3>
                    <p className="mt-1 text-sm text-slate-300">{ms.desc}</p>
                  </div>

                  {/* Spacer for the other side */}
                  <div className="hidden flex-1 lg:block" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Team ─────────────────────────────────────── */}
      <section className="bg-background py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto mb-16 max-w-2xl text-center">
            <h2 className="text-3xl font-bold text-white">Leadership Team</h2>
            <p className="mt-3 text-base text-slate-300">
              Experienced operators, engineers, and domain experts building the future of fleet intelligence.
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {team.map((member, i) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="rounded-2xl border border-white/10 bg-background p-6 glass-card text-center"
              >
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-neon-cyan/10 text-xl font-bold text-neon-cyan">
                  {member.name.split(' ').map(n => n[0]).join('')}
                </div>
                <h3 className="font-semibold text-white">{member.name}</h3>
                <p className="text-sm font-medium text-neon-cyan">{member.role}</p>
                <p className="mt-2 text-sm text-slate-300">{member.bio}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Compliance ───────────────────────────────── */}
      <section className="bg-white/5 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto mb-16 max-w-2xl text-center">
            <h2 className="text-3xl font-bold text-white">Security & Compliance</h2>
            <p className="mt-3 text-base text-slate-300">
              Enterprise-grade security is not optional. It is foundational to everything we build.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {compliance.map((c) => (
              <div key={c.label} className="flex items-start gap-3 rounded-2xl border border-white/10 bg-background p-6 glass-card">
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" />
                <div>
                  <h3 className="font-semibold text-white">{c.label}</h3>
                  <p className="text-sm text-slate-300">{c.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Contact ──────────────────────────────────── */}
      <section id="contact" className="bg-background py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center mb-12">
            <h2 className="text-3xl font-bold text-white">Get In Touch</h2>
            <p className="mt-3 text-base text-slate-300">
              Whether you are scaling fleets, building insurance products, or modernizing operations, we are ready to help.
            </p>
          </div>
          <div className="mx-auto grid max-w-3xl gap-6 sm:grid-cols-3">
            <div className="rounded-2xl border border-white/10 bg-background p-6 text-center shadow-sm">
              <Building2 className="mx-auto mb-3 h-6 w-6 text-emerald-500" />
              <h3 className="font-semibold text-white">Office</h3>
              <p className="mt-1 text-sm text-slate-300">Sandton, Johannesburg<br />South Africa</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-background p-6 text-center shadow-sm">
              <Mail className="mx-auto mb-3 h-6 w-6 text-emerald-500" />
              <h3 className="font-semibold text-white">Email</h3>
              <p className="mt-1 text-sm text-slate-300">connect@glonextrack.com</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-background p-6 text-center shadow-sm">
              <Phone className="mx-auto mb-3 h-6 w-6 text-emerald-500" />
              <h3 className="font-semibold text-white">Phone</h3>
              <p className="mt-1 text-sm text-slate-300">+27 (0)10 500 1234</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────── */}
      <section className="bg-gradient-to-br from-emerald-600 to-teal-500 py-16">
        <div className="mx-auto max-w-4xl px-6 text-center lg:px-8">
          <h2 className="text-3xl font-bold text-white">Ready to join the GlonexTrack ecosystem?</h2>
          <p className="mx-auto mt-3 max-w-2xl text-emerald-50">
            Start your 14-day free trial today and experience the future of fleet intelligence.
          </p>
          <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
            <Link href="/signup">
              <Button size="lg" className="rounded-full bg-white text-emerald-700 hover:bg-emerald-50">
                Start Free Trial <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
